import { ceil } from "~/tools/ceil";
import { clamp } from "~/tools/clamp";
import { floor } from "~/tools/floor";
import { max } from "~/tools/max";
import { min } from "~/tools/min";
import { round } from "~/tools/round";
import { sqrt } from "~/tools/sqrt";

interface CalculateCellsFitParams {
	width: number;
	height: number;
	minCellSize: number;
}

/**
 * Maximum number of square cells of side at least `minCellSize` that fit into a
 * `width x height` area.
 */
function calculateCellsFit({ width, height, minCellSize }: CalculateCellsFitParams): number {
	return max(1, floor(width / minCellSize, 0) * floor(height / minCellSize, 0));
}

/**
 * Picks the column/row counts that keep each cell as square as possible for the
 * given container dimensions and item count.
 *
 * cols / rows = width / height
 * cols * rows = count
 * => cols = sqrt((count * width) / height)
 */
function calculateProportionalColumns({
	width,
	height,
	count,
}: {
	width: number;
	height: number;
	count: number;
}): [cols: number, rows: number] {
	if (width <= 0 || height <= 0 || count <= 1) {
		return [1, 1];
	}

	const maxCols = sqrt((count * width) / height);
	const cols = min(count, round(maxCols, 0));

	if (cols === 0) {
		return [1, 1];
	}

	return [max(1, cols), max(1, ceil(count / cols, 0))];
}

/**
 * Inter-cell gap sized as 10% of a column's width, clamped to [8, 32] and
 * rounded to the nearest multiple of 4.
 */
function calculateColsGap(width: number, cols: number): number {
	const colWidth = width / max(1, cols);
	const gap = clamp(colWidth / 10, { min: 8, max: 32 });
	const step = 4;

	return round(gap / step, 0) * step;
}

interface CalculateGridLayoutParams extends CalculateCellsFitParams {
	count: number;
}

/**
 * Lays out up to `count` square-ish cells into a `width x height` area while
 * respecting `minCellSize`.
 */
function calculateGridLayout({ width, height, minCellSize, count }: CalculateGridLayoutParams): {
	cols: number;
	rows: number;
	gap: number;
} {
	const maxCells = calculateCellsFit({ width, height, minCellSize });
	const limit = maxCells < count ? maxCells - 1 : count;

	const [cols, rows] = calculateProportionalColumns({
		width,
		height,
		count: min(count, limit + 1),
	});

	return {
		cols,
		rows,
		gap: calculateColsGap(width, cols),
	};
}

export { calculateCellsFit, calculateGridLayout };
export type { CalculateCellsFitParams, CalculateGridLayoutParams };
